import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SettingToken, SiteToken } from '@sleed/bizcommerce-api';
import { settingsApi, sitesApi } from '~/lib/api/client';
import { formToToken, type SettingFormValues } from '~/lib/schemas/setting';

export const settingKeys = {
	all: ['settings'] as const,
	list: () => [...settingKeys.all, 'list'] as const,
	names: () => [...settingKeys.all, 'names'] as const,
	sites: () => [...settingKeys.all, 'sites'] as const
};

/** Full settings list (legacy `/settings/getall`, loadMode 'raw' — all rows, no server paging). */
export function useSettingsList() {
	return useQuery<SettingToken[]>({
		queryKey: settingKeys.list(),
		queryFn: async () => (await settingsApi.getAll()).data.items ?? []
	});
}

/**
 * Distinct known setting names for the ParamName hybrid editor (legacy `GetDefaultSettings`).
 * Returns a sorted unique list of `pn` values — drives the datalist suggestions.
 */
export function useSettingNames() {
	return useQuery<string[]>({
		queryKey: settingKeys.names(),
		queryFn: async () => {
			const defaults = (await settingsApi.getDefaultSettings()).data ?? [];
			const names = defaults.map(s => s.pn?.trim()).filter((n): n is string => Boolean(n));
			return [...new Set(names)].sort((a, b) => a.localeCompare(b));
		},
		staleTime: 5 * 60 * 1000
	});
}

/** Sites lookup ({id, nm}) for the site-override (`wsid`) column + dropdown. */
export function useSites() {
	return useQuery<SiteToken[]>({
		queryKey: settingKeys.sites(),
		queryFn: async () => (await sitesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

/** Create / update / delete a setting (parity with the legacy CustomStore insert/update/remove). */
export function useSettingMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: settingKeys.all });

	const create = useMutation({
		mutationFn: async (form: SettingFormValues) => (await settingsApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async (form: SettingFormValues) => (await settingsApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await settingsApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
